import { Box, Tooltip } from "@mui/material";
import {
  disabledFeatureTooltipSlotProps,
  disabledFeatureUpsellMessage,
} from "constants/DISABLED_FEATURE";
import ROLE from "enums/ROLE_NAME";
import useGetProjectFeatures from "hooks/api/useGetProjectFeatures";
import useCurrentUser from "hooks/useCurrentUser";
import useProject from "hooks/useProject";
import { ReactNode } from "react";

const UPSELL_BLOCKED_ROLES: ROLE[] = [ROLE.VIEWER, ROLE.ADMIN_NOPII];

export function shouldShowDisabledFeatureUpsell(role: ROLE): boolean {
  return !UPSELL_BLOCKED_ROLES.includes(role);
}

export interface FeatureGateProps {
  /** Catalog `feature_name` from GET project-features (`AccountFeatureType` enum string). */
  featureName?: string;
  /** Upsell copy; defaults to `DISABLED_FEATURE` when `featureName` is set. */
  title?: string | undefined;
  children: ReactNode;
  /** MUI Tooltip placement (sidebar uses `right`) */
  placement?: "top" | "right" | "bottom" | "left";
  /**
   * When set, blocks interaction when true (e.g. account project cap).
   * Skips project-feature catalog checks.
   */
  blocked?: boolean;
}

function BlockedOverlay({
  title,
  placement,
  children,
  upsellTooltip = false,
}: {
  title?: string | undefined;
  placement: FeatureGateProps["placement"];
  children: ReactNode;
  upsellTooltip?: boolean;
}) {
  return (
    <Tooltip
      title={title}
      placement={placement}
      slotProps={upsellTooltip ? disabledFeatureTooltipSlotProps : undefined}
    >
      <Box
        component="span"
        sx={{
          position: "relative",
          display: "inline-flex",
          alignItems: "stretch",
          maxWidth: "100%",
        }}
      >
        {children}
        <Box
          component="span"
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          sx={{
            position: "absolute",
            inset: 0,
            pointerEvents: "auto",
            cursor: "default",
          }}
        />
      </Box>
    </Tooltip>
  );
}

/**
 * If the project has the feature: render children.
 * If missing the feature and the role may see upsell: tooltip + block overlay.
 * Otherwise: render nothing.
 *
 * When `blocked` is set: render children when false; tooltip + block overlay when true.
 */
export default function FeatureGate({
  featureName,
  title,
  children,
  placement = "top",
  blocked,
}: FeatureGateProps) {
  const { projectId } = useProject();
  const { role } = useCurrentUser();
  const { features } = useGetProjectFeatures(projectId);

  if (blocked !== undefined) {
    if (!blocked) {
      return <>{children}</>;
    }
    return (
      <BlockedOverlay title={title} placement={placement}>
        {children}
      </BlockedOverlay>
    );
  }

  const hasFeature = features.some((f) => f.featureName === featureName);
  const upsellTitle =
    (featureName ? disabledFeatureUpsellMessage(featureName) : undefined) ??
    title ??
    "";

  if (hasFeature) {
    return <>{children}</>;
  }

  if (shouldShowDisabledFeatureUpsell(role)) {
    return (
      <BlockedOverlay title={upsellTitle} placement={placement} upsellTooltip>
        {children}
      </BlockedOverlay>
    );
  }

  return null;
}
